import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { EXPERIENCE as EXPERIENCE_FALLBACK, TESTIMONIALS as TESTIMONIALS_FALLBACK, BRAND } from "../data/content";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import PhotoRow from "../components/media/PhotoRow";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  "All Experience",
  "Leadership",
  "Digital Transformation",
  "Operations",
  "Programs & Projects",
  "Major Events",
  "Community Engagement",
];

const CHAPTERS = [
  { n: "01", title: "Foundations in Operations & Hospitality", body: "Early service delivery, customer experience, operational responsibility and leadership foundations.", tag: "Operations" },
  { n: "02", title: "Student Experience & Community Engagement", body: "Student programs, engagement, inclusion, community initiatives and stakeholder relationships.", tag: "Community Engagement" },
  { n: "03", title: "Major Events & Large-Scale Operations", body: "Large-scale events, operational delivery, sponsorship, workforce coordination and international experience.", tag: "Major Events" },
  { n: "04", title: "University Programs, Projects & Systems", body: "Higher education, program development, operational improvement, project leadership and systems design.", tag: "Programs & Projects" },
  { n: "05", title: "Member Engagement & Digital Transformation", body: "Australian Services Union, September 2026 onwards. Team leadership, membership operations, CRM, workflow design and practical digital transformation.", tag: "Digital Transformation" },
];

const ASU_AREAS = [
  { n: "01", title: "People & Member Experience", body: "Leading the Member Engagement Team across frontline member enquiries, membership administration and service delivery, with a focus on supportive leadership, effective workload coordination and responsive member experiences.", examples: ["Introducing regular team huddles and more structured operational communication", "Conducting staff development conversations and structured feedback", "Improving visibility of workload, staffing coverage and competing service priorities", "Clarifying team responsibilities and frontline service expectations"] },
  { n: "02", title: "Operational Transformation", body: "Reviewing and improving how member enquiries, membership administration and Industrial Support referrals move through the organisation — creating clearer responsibilities, more consistent processes and better visibility over work.", examples: ["Reviewing shared Membership and Info inbox processes", "Developing clearer triage, allocation and referral guidance", "Improving coordination between the Member Engagement Team and Industrial Support Team", "Introducing structured booking capacity and manager-approved escalation arrangements"] },
  { n: "03", title: "CRM & Digital Systems", body: "Overseeing and improving the use of iMIS and connected workplace technologies, translating frontline operational challenges into clearer workflows, stronger data practices and more useful digital tools.", examples: ["Reviewing iMIS membership and case-management processes", "Investigating data quality and membership record inconsistencies", "Developing more consistent CRM documentation and record-handling practices", "Exploring improvements across Microsoft Teams, SharePoint, Outlook and other existing platforms"] },
  { n: "04", title: "Automation & Reporting", body: "Designing and testing practical workflow automation and operational reporting improvements, reducing reliance on manual coordination while keeping staff judgement, accountability and member experience central.", examples: ["Building and testing Power Automate workflows for referral and handoff tracking", "Developing status, follow-up and overdue monitoring processes", "Testing workflows using fictional records before live operational use", "Considering privacy, governance and human oversight when introducing automation"] },
];

const ASU_CAPS = ["People Leadership","Member Experience","Operational Improvement","CRM & iMIS","Workflow Design","Microsoft 365","Power Automate","Teams & SharePoint","Data & Reporting","Change Management"];

const PRINCIPLES = [
  { n: "01", kicker: "Systems", headline: "Make complex work simpler.", body: "Understand how work actually happens, identify unnecessary complexity and design clearer processes and practical systems." },
  { n: "02", kicker: "Impact", headline: "Build change that lasts.", body: "Create meaningful improvements that can be measured, maintained and developed over time." },
  { n: "03", kicker: "People", headline: "Design around humans.", body: "Support the people doing the work and ensure systems improve their experience rather than create additional complexity." },
];

const FEATURED_ID = "shawn-kazubowski-houston";
const CAROUSEL_IDS = ["barbra-yan","bavatharani-maheswaran","victoria-mercy-kataike"];

const BEYOND_ITEMS = [
  { title: "Community Involvement", caption: "JJ's Community Support · Melbourne, during COVID-19", src: "/assets/jjs-community-support-1.jpg", span: "lg:col-span-7 lg:row-span-2", aspect: "aspect-[4/3]" },
  { title: "International Experience", caption: "Australian High Commissioner reception · Toronto", src: "/assets/jcu-canada-representation-invite.jpg", span: "lg:col-span-5", aspect: "aspect-[4/3]" },
  { title: "Harm reduction & inclusion", caption: "Thorne Harbour Health volunteer days", src: "/assets/thorne-harbour-volunteer-4.webp", span: "lg:col-span-5", aspect: "aspect-[4/3]" },
  { title: "Events & activations", caption: "UMSU IDAHOBIT Day — bringing people together", src: "/assets/umsu-event-attendees.jpg", span: "lg:col-span-5", aspect: "aspect-[4/3]" },
  { title: "Representation & advocacy", caption: "Representing JCU in Canada", src: "/assets/jjs-community-support-3.jpg", span: "lg:col-span-7", aspect: "aspect-[16/9]" },
];

export default function About() {
  useDocumentMeta({
    title: "About · Different roles. Same problem-solving brain. — JJ Argent",
    description: "About JJ Argent — Member Engagement & Digital Transformation Manager at the Australian Services Union. 13+ years across universities, major events and community initiatives in Australia and Canada.",
    path: "/about",
  });

  const [filter, setFilter] = useState("All Experience");
  const [experience, setExperience] = useState(EXPERIENCE_FALLBACK);
  const [volunteers, setVolunteers] = useState([]);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS_FALLBACK);
  const [openId, setOpenId] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [asuAreaIdx, setAsuAreaIdx] = useState(0);

  const experienceRef = useRef(null);

  const roleKey = useCallback((e) => e.id || e.org, []);

  useEffect(() => {
    axios.get(`${API}/experiences`).then((r) => {
      if (!Array.isArray(r.data)) return;
      const paid = r.data.filter((x) => (x.kind || "paid") === "paid");
      const vols = r.data.filter((x) => x.kind === "volunteer");
      if (paid.length > 0) {
        setExperience(paid);
        const asu = paid.find((p) => p.org === "Australian Services Union");
        if (asu) setOpenId(asu.id || asu.org);
      }
      setVolunteers(vols);
    }).catch(() => {
      const asu = EXPERIENCE_FALLBACK.find((p) => p.org === "Australian Services Union");
      if (asu) setOpenId(asu.id || asu.org);
    });
    axios.get(`${API}/testimonials`).then((r) => {
      if (Array.isArray(r.data) && r.data.length > 0) {
        const byId = new Map();
        TESTIMONIALS_FALLBACK.forEach((t) => byId.set(t.id || t.slug, t));
        r.data.forEach((t) => {
          const key = t.id || t.slug;
          byId.set(key, { ...byId.get(key), ...t, id: key });
        });
        setTestimonials(Array.from(byId.values()));
      }
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (filter === "All Experience") return experience;
    return experience.filter((e) => (e.tags || []).includes(filter));
  }, [filter, experience]);

  useEffect(() => {
    if (filtered.length === 0) { setOpenId(null); return; }
    const visibleHasOpen = filtered.some((e) => roleKey(e) === openId);
    if (!visibleHasOpen) setOpenId(roleKey(filtered[0]));
  }, [filtered, openId, roleKey]);

  const scrollToExperience = useCallback(() => {
    if (experienceRef.current) {
      experienceRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const onChapterClick = (c, i) => {
    setActiveChapter(i);
    setFilter(c.tag);
    setTimeout(scrollToExperience, 50);
  };

  const resetAll = () => { setActiveChapter(null); setFilter("All Experience"); };

  const featured = testimonials.find((t) => (t.id || t.slug) === FEATURED_ID);
  const carousel = CAROUSEL_IDS.map((id) => testimonials.find((t) => (t.id || t.slug) === id)).filter(Boolean);
  const currentCarousel = carousel[carouselIdx] || null;

  return (
    <main id="main" data-testid="about-page" className="bg-[#0B0B0C] text-[#FAF9F6]">
      {/* ========= SECTION 1 — About introduction ========= */}
      <section className="pt-28 lg:pt-32 pb-16 lg:pb-20 relative grain" aria-labelledby="about-hero-title">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow">About · JJ Argent</p>
              <h1 id="about-hero-title" className="mt-5 font-display leading-[0.98] tracking-[-0.03em] text-[#FAF9F6]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }} data-testid="about-h1">
                Different roles. <br />
                <span className="text-[#D8A936]">Same</span> problem-solving brain.
              </h1>
              <div className="mt-7 space-y-4 text-[#A9A9A6] leading-relaxed max-w-2xl text-[15.5px]">
                <p>Across Australia and Canada, I've built my career at the intersection of people, operations and systems.</p>
                <p>From major events and student experience to university programs, community initiatives and member engagement, I've repeatedly found myself working on the same challenge: making complex operations simpler, helping people succeed and building systems that create lasting impact.</p>
                <p>Today, I lead Member Engagement and Digital Transformation at the Australian Services Union's Victorian and Tasmanian Authorities & Services Branch — connecting frontline service delivery, team leadership, CRM improvement and practical digital transformation.</p>
                <p className="text-[#FAF9F6]">Different sectors. Different challenges. The same commitment to making things work better.</p>
              </div>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-8 border-t border-[#343437] pt-8">
                {[{ k: "People", d: "Leadership, collaboration and experience." },{ k: "Systems", d: "Operations, processes and transformation." },{ k: "Impact", d: "Meaningful results that continue to deliver." }].map((p) => (
                  <div key={p.k} data-testid={`about-pillar-${p.k.toLowerCase()}`}>
                    <span aria-hidden className="block w-8 h-px bg-[#D8A936] mb-3" />
                    <p className="font-display text-[11px] tracking-[0.28em] uppercase text-[#D8A936]">{p.k}</p>
                    <p className="mt-2 text-sm text-[#A9A9A6] leading-relaxed">{p.d}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] max-h-[72vh] overflow-hidden">
                <picture>
                  <source type="image/webp" srcSet={BRAND.portraitSrcSet} sizes="(min-width: 1024px) 42vw, 92vw" />
                  <img
                    src={BRAND.portrait}
                    srcSet={BRAND.portraitSrcSetJpg}
                    sizes="(min-width: 1024px) 42vw, 92vw"
                    alt="JJ Argent — portrait"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)", maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 65%, rgba(0,0,0,0) 100%)" }}
                    data-testid="about-portrait"                  />
                </picture>
                <p className="absolute bottom-3 left-0 font-signature text-2xl text-[#D8A936] rotate-[-4deg]">JJ Argent</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========= SECTION 2 — Current Chapter: ASU ========= */}
      <section className="bg-[#FAF9F6] text-[#0B0B0C] relative overflow-hidden" aria-labelledby="asu-title" data-testid="asu-current-chapter">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-6">
              <p className="eyebrow !text-[#B98B24]">Current chapter · Sep 2026 — Present</p>
              <h2 id="asu-title" className="mt-4 font-display leading-[1.02] tracking-[-0.02em] text-[#0B0B0C]" style={{ fontSize: "clamp(1.75rem, 3.6vw, 3rem)" }} data-testid="asu-h2">
                Member Engagement <span className="text-[#B98B24]">&</span> Digital Transformation Manager
              </h2>
              <div className="mt-5 flex items-center gap-4 flex-wrap">
                <img src="/assets/asu-logo.png" alt="Australian Services Union" className="h-11 w-auto opacity-90 shrink-0" loading="lazy" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                <div className="min-w-0">
                  <p className="font-display text-base md:text-lg">Australian Services Union</p>
                  <p className="text-[11.5px] tracking-[0.14em] uppercase text-[#343437] font-display">Victorian &amp; Tasmanian Authorities &amp; Services Branch · Melbourne</p>
                </div>
              </div>
              <div className="mt-6 space-y-4 text-[#343437] leading-relaxed max-w-xl text-[15px]">
                <p>Leading member engagement operations, frontline service delivery and digital transformation across the Victorian and Tasmanian Branch.</p>
                <p>My role brings together people leadership, member experience, CRM improvement, operational workflow design and practical automation — helping teams deliver more consistent, effective and sustainable member support.</p>
              </div>

              <button type="button" onClick={() => { setFilter("All Experience"); setTimeout(scrollToExperience, 50); }} data-testid="asu-explore-cta" className="mt-8 inline-flex items-center gap-2 font-display text-[11.5px] tracking-[0.18em] uppercase text-[#0B0B0C] border-b border-[#0B0B0C]/40 pb-1 hover:border-[#B98B24] hover:text-[#B98B24] transition-colors">
                Explore my experience <ArrowUpRight size={14} />
              </button>
            </div>

            <div className="lg:col-span-6 space-y-4">
              <figure className="overflow-hidden" data-testid="asu-team-photo-wide">
                <img src="/assets/asu-team.jpg" alt="Australian Services Union — Victorian & Tasmanian Branch team. JJ Argent seated front-centre wearing a blue-and-black striped shirt." className="w-full h-auto block" loading="lazy" />
                <figcaption className="mt-3 text-[11px] tracking-[0.16em] uppercase text-[#343437]">ASU Vic/Tas team · 2026</figcaption>
              </figure>
              <figure className="overflow-hidden hidden md:block" data-testid="asu-team-photo-crop">
                <div className="aspect-[21/6] overflow-hidden">
                  <img src="/assets/asu-team.jpg" alt="ASU team — tighter crop with JJ visible among colleagues" className="w-full h-full object-cover" style={{ objectPosition: "48% 72%" }} loading="lazy" />
                </div>
              </figure>
            </div>
          </div>

          <div className="mt-16 lg:mt-20 border-t border-[#0B0B0C]/15 pt-10 lg:pt-14">
            <div className="flex items-center gap-3 flex-wrap mb-6">
              {ASU_AREAS.map((a, i) => (
                <button key={a.n} type="button" onClick={() => setAsuAreaIdx(i)} data-testid={`asu-area-tab-${i}`} className={`px-3.5 py-2 font-display text-[11px] tracking-[0.16em] uppercase border transition-colors ${asuAreaIdx === i ? "border-[#B98B24] text-[#B98B24] bg-[#B98B24]/5" : "border-[#0B0B0C]/20 text-[#343437] hover:border-[#0B0B0C] hover:text-[#0B0B0C]"}`}>
                  {a.n} · {a.title}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={asuAreaIdx} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }} className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid={`asu-area-panel-${asuAreaIdx}`}>
                <div className="lg:col-span-5">
                  <p className="eyebrow !text-[#B98B24]">Area {ASU_AREAS[asuAreaIdx].n}</p>
                  <h3 className="mt-3 font-display text-2xl md:text-3xl leading-tight tracking-tight">{ASU_AREAS[asuAreaIdx].title}</h3>
                  <p className="mt-4 text-[#343437] leading-relaxed text-[15px]">{ASU_AREAS[asuAreaIdx].body}</p>
                </div>
                <div className="lg:col-span-7">
                  <p className="eyebrow !text-[#343437]">In practice</p>
                  <ul className="mt-3 space-y-3">
                    {ASU_AREAS[asuAreaIdx].examples.map((ex) => (
                      <li key={ex} className="pl-4 border-l border-[#B98B24]/40 text-[#343437] leading-relaxed text-[14.5px]">{ex}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 pt-8 border-t border-[#0B0B0C]/15">
              <p className="eyebrow !text-[#343437]">Working across</p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                {ASU_CAPS.map((c) => (
                  <span key={c} className="font-display text-[11px] tracking-[0.14em] uppercase text-[#0B0B0C]/80">{c}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========= SECTION 3 — Interactive 5-chapter career timeline ========= */}
      <section className="bg-[#0B0B0C] border-t border-[#343437] relative grain" aria-labelledby="journey-title" data-testid="career-journey">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
            <div className="lg:col-span-8">
              <p className="eyebrow">My career journey</p>
              <h2 id="journey-title" className="mt-4 font-display leading-[1.02] tracking-[-0.02em] text-[#FAF9F6]" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                A career built on people, programs <span className="text-[#D8A936]">and possibilities.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-[#A9A9A6] leading-relaxed text-[14.5px] max-w-sm">Different roles, industries and countries. A consistent focus on solving problems, bringing people together and creating systems that make a real difference.</p>
            </div>
          </div>

          <div className="hidden lg:block relative h-10 mb-6" aria-hidden>
            <svg viewBox="0 0 1200 40" className="w-full h-full" preserveAspectRatio="none">
              <motion.path d="M 20 32 C 220 28, 320 20, 600 14 S 960 6, 1180 4" stroke="#D8A936" strokeWidth="1.4" strokeLinecap="round" fill="none" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1.6, ease: [0.16, 0.84, 0.24, 1] }} />
            </svg>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-5">
            {CHAPTERS.map((c, i) => {
              const active = activeChapter === i;
              return (
                <button key={c.n} type="button" onClick={() => onChapterClick(c, i)} data-testid={`chapter-${c.n}`} className={`text-left border p-5 lg:p-6 transition-all duration-300 relative group ${active ? "border-[#D8A936] bg-[#D8A936]/8" : "border-[#343437] hover:border-[#D8A936]/60 bg-[#0B0B0C]"}`}>
                  <span aria-hidden className={`block w-2 h-2 rounded-full absolute -top-1 left-5 ${active ? "bg-[#D8A936]" : "bg-[#343437] group-hover:bg-[#D8A936]/60"}`} />
                  <p className={`font-display text-[11px] tracking-[0.2em] uppercase ${active ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>Chapter {c.n}</p>
                  <h3 className="mt-3 font-display text-base lg:text-[17px] leading-tight text-[#FAF9F6]">{c.title}</h3>
                  <p className="mt-3 text-[12.5px] text-[#A9A9A6] leading-relaxed">{c.body}</p>
                </button>
              );
            })}
          </div>

          {activeChapter !== null && (
            <div className="mt-8">
              <button type="button" onClick={resetAll} data-testid="chapter-reset" className="text-[11.5px] tracking-[0.18em] uppercase text-[#A9A9A6] border-b border-[#343437] pb-1 hover:text-[#D8A936] hover:border-[#D8A936] transition-colors">
                ← Return to All Experience
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ========= SECTION 4 — Detailed Professional Experience ========= */}
      <section ref={experienceRef} id="experience" className="py-20 lg:py-24 scroll-mt-20" aria-labelledby="experience-title" data-testid="experience-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow">Experience</p>
              <h2 id="experience-title" className="mt-4 font-display leading-[1.02] tracking-[-0.02em] text-[#FAF9F6]" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                A closer look at <span className="text-[#D8A936]">my career.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#A9A9A6] leading-relaxed text-[14.5px] max-w-md">Explore the roles, projects and achievements behind my work across leadership, operations, programs and digital transformation.</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10" role="tablist" aria-label="Capability filters">
            {FILTERS.map((t) => (
              <button key={t} type="button" role="tab" aria-selected={filter === t} onClick={() => { setFilter(t); if (t === "All Experience") setActiveChapter(null); }} data-testid={`exp-filter-${t.toLowerCase().replace(/[&\s]+/g, "-")}`} className={`px-3.5 py-2 font-display text-[11px] tracking-[0.16em] uppercase border transition-colors ${filter === t ? "border-[#D8A936] text-[#D8A936] bg-[#D8A936]/5" : "border-[#343437] text-[#A9A9A6] hover:border-[#FAF9F6] hover:text-[#FAF9F6]"}`}>
                {t}
              </button>
            ))}
          </div>

          <div className="border-t border-[#343437]">
            {filtered.map((e, i) => {
              const key = roleKey(e);
              const isOpen = openId === key;
              const isASU = e.org === "Australian Services Union";
              return (
                <article key={key} className="border-b border-[#343437]" data-testid={`experience-${i}`}>
                  <button type="button" onClick={() => setOpenId(isOpen ? null : key)} aria-expanded={isOpen} aria-controls={`exp-panel-${i}`} className="w-full text-left py-6 lg:py-8 grid grid-cols-1 lg:grid-cols-12 gap-5 items-start group">
                    <div className="lg:col-span-2">
                      <p className={`font-display text-[11.5px] tracking-[0.18em] uppercase ${isASU ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>{e.period}</p>
                      {e.location && <p className="mt-2 text-xs text-[#A9A9A6]">{e.location}</p>}
                    </div>
                    <div className="lg:col-span-9">
                      <h3 className="font-display text-xl md:text-2xl leading-tight tracking-tight text-[#FAF9F6]">{e.role}</h3>
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        {e.org_logo?.src && (
                          <img src={e.org_logo.src} alt={e.org_logo.alt || `${e.org} logo`} loading="lazy" className="h-5 w-auto max-w-[100px] object-contain opacity-85" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                        )}
                        <p className="text-[#D8A936] font-display text-[12.5px] tracking-[0.12em] uppercase">{e.org}</p>
                      </div>
                      {!isOpen && (e.overview || e.bullets?.[0]) && (
                        <p className="mt-3 text-[14px] text-[#A9A9A6] leading-relaxed line-clamp-2 max-w-3xl">{e.overview || e.bullets[0]}</p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(e.tags || []).map((t) => (
                          <span key={t} className="text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] border border-[#343437] px-2 py-0.5">{t}</span>
                        ))}
                      </div>
                    </div>
                    <div className="lg:col-span-1 flex lg:justify-end items-start pt-1">
                      <ChevronDown size={20} className={`text-[#A9A9A6] transition-transform duration-300 ${isOpen ? "rotate-180 text-[#D8A936]" : ""}`} aria-hidden />
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div id={`exp-panel-${i}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.16, 0.84, 0.24, 1] }} className="overflow-hidden" data-testid={`experience-${i}-panel`}>
                        <div className="pb-8 lg:pb-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
                          <div className="lg:col-start-3 lg:col-span-9">
                            {e.overview && (
                              <p className="text-[15px] text-[#FAF9F6]/90 leading-relaxed max-w-3xl mb-5">{e.overview}</p>
                            )}
                            <ul className="space-y-3 text-[#A9A9A6] leading-relaxed text-[14.5px]">
                              {(e.bullets || []).map((b, bi) => (
                                <li key={`${key}-${bi}`} className="pl-4 border-l border-[#D8A936]/30">{b}</li>
                              ))}
                            </ul>
                            {Array.isArray(e.gallery) && e.gallery.length > 0 && (
                              <div className="mt-6">
                                <PhotoRow testId={`experience-${i}-gallery`} photos={e.gallery.map((g) => ({ src: g.src, alt: g.alt, caption: g.caption }))} />
                              </div>
                            )}
                            {e.org_url && (
                              <a href={e.org_url} target="_blank" rel="noreferrer noopener" className="mt-6 inline-flex items-center gap-2 text-[#D8A936] font-display text-[11.5px] tracking-[0.18em] uppercase border-b border-[#D8A936]/40 pb-1 hover:border-[#D8A936]">
                                {e.org_link_label || `Visit ${e.org}`} <ArrowUpRight size={13} />
                              </a>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </article>
              );
            })}
            {filtered.length === 0 && (
              <p className="py-10 text-[#A9A9A6]" data-testid="experience-empty">No roles match this filter.</p>
            )}
          </div>
        </div>
      </section>

      {/* ========= SECTION 5 — How I Work ========= */}
      <section className="bg-[#FAF9F6] text-[#0B0B0C] border-t border-[#343437]" aria-labelledby="how-i-work-title" data-testid="how-i-work">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10">
            <div className="lg:col-span-8">
              <p className="eyebrow !text-[#B98B24]">How I work</p>
              <h2 id="how-i-work-title" className="mt-3 font-display leading-[0.98] tracking-[-0.02em] text-[#0B0B0C]" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                Systems. <span className="text-[#B98B24]">·</span> Impact. <span className="text-[#B98B24]">·</span> People.
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-[#343437] text-[14.5px] max-w-sm">Different challenges. Same approach.</p>
            </div>
          </div>

          <div className="hidden lg:block relative h-6 mb-8" aria-hidden>
            <svg viewBox="0 0 1200 24" className="w-full h-full" preserveAspectRatio="none">
              <motion.path d="M 20 20 C 300 14, 600 6, 1180 4" stroke="#B98B24" strokeWidth="1.3" strokeLinecap="round" fill="none" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 1.4, ease: [0.16, 0.84, 0.24, 1] }} />
            </svg>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            {PRINCIPLES.map((p) => (
              <article key={p.kicker} data-testid={`how-i-work-${p.kicker.toLowerCase()}`}>
                <span aria-hidden className="block w-10 h-px bg-[#B98B24] mb-4" />
                <p className="font-display text-[11px] tracking-[0.28em] uppercase text-[#B98B24]">{p.n} · {p.kicker}</p>
                <h3 className="mt-3 font-display text-xl md:text-2xl leading-tight tracking-tight text-[#0B0B0C] max-w-xs">{p.headline}</h3>
                <p className="mt-3 text-[14.5px] text-[#343437] leading-relaxed max-w-sm">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ========= SECTION 6 — Beyond the CV ========= */}
      <section className="bg-[#0B0B0C] border-t border-[#343437] py-20 lg:py-24" aria-labelledby="beyond-title" data-testid="beyond-cv">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow">Beyond the CV</p>
              <h2 id="beyond-title" className="mt-4 font-display leading-[1.02] tracking-[-0.02em] text-[#FAF9F6]" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                A broader part <span className="text-[#D8A936]">of the story.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#A9A9A6] leading-relaxed text-[14.5px] max-w-md">Outside formal job titles, I've always been drawn to community, creativity and experiences that bring people together. From volunteering and community initiatives to living and working internationally, those experiences have shaped how I approach leadership, collaboration and the work I do.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
            {BEYOND_ITEMS.map((item, i) => (
              <motion.figure key={item.src} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.06 }} className={`${item.span} overflow-hidden border border-[#343437] bg-[#151517] group`} data-testid={`beyond-item-${i}`}>
                <div className={`${item.aspect} relative`}>
                  <img src={item.src} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                </div>
                <figcaption className="px-4 py-3 border-t border-[#343437]">
                  <p className="font-display text-[11px] tracking-[0.18em] uppercase text-[#D8A936]">{item.title}</p>
                  <p className="mt-1 text-[12.5px] text-[#A9A9A6] leading-snug">{item.caption}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          {volunteers.length > 0 && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 border-t border-[#343437] pt-10">
              {volunteers.slice(0, 4).map((v) => (
                <div key={v.id || v.org} className="border-l-2 border-[#D8A936]/60 pl-4" data-testid={`volunteer-${v.id || v.org}`}>
                  <p className="font-display text-[14.5px] text-[#FAF9F6] leading-tight">{v.role}</p>
                  <p className="text-[#D8A936] font-display text-[11px] tracking-[0.14em] uppercase mt-1">{v.org}</p>
                  <p className="text-[11px] text-[#A9A9A6] mt-1">{v.period}{v.location ? ` · ${v.location}` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ========= SECTION 7 — Testimonials ========= */}
      <section className="bg-[#151517] border-t border-[#343437] py-20 lg:py-24" aria-labelledby="testimonials-title" data-testid="about-testimonials">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow">What others say</p>
              <h2 id="testimonials-title" className="mt-4 font-display leading-[1.02] tracking-[-0.02em] text-[#FAF9F6]" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                Real people. <span className="text-[#D8A936]">Real impact.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            {featured && (
              <figure className="lg:col-span-7 bg-[#0B0B0C] border border-[#343437] p-8 lg:p-12 relative" data-testid="testimonial-featured">
                <span aria-hidden className="absolute -top-6 -left-2 font-display text-[160px] leading-none text-[#D8A936]/15 select-none pointer-events-none">"</span>
                <blockquote className="relative font-display text-2xl md:text-3xl lg:text-[32px] leading-[1.2] tracking-[-0.01em] text-[#FAF9F6]">{featured.quote}</blockquote>
                <figcaption className="relative mt-8 pt-6 border-t border-[#343437]">
                  <p className="font-display text-base text-[#FAF9F6]">{featured.name}</p>
                  {featured.title && <p className="text-[12px] text-[#A9A9A6] mt-1 leading-snug">{featured.title}</p>}
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#D8A936] mt-2">{featured.org}</p>
                  {featured.source && <p className="text-[10px] tracking-[0.18em] uppercase text-[#6E6E6B] mt-1">{featured.source}</p>}
                  {featured.theme && (
                    <span className="mt-3 inline-block text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] border border-[#343437] px-2 py-0.5">{featured.theme}</span>
                  )}
                </figcaption>
              </figure>
            )}

            {currentCarousel && (
              <div className="lg:col-span-5 flex flex-col" data-testid="testimonial-carousel">
                <AnimatePresence mode="wait">
                  <motion.figure key={currentCarousel.id || currentCarousel.slug} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.35 }} className="bg-[#0B0B0C] border border-[#343437] p-7 lg:p-9 flex-1 flex flex-col" data-testid={`testimonial-carousel-item-${carouselIdx}`}>
                    <span aria-hidden className="font-display text-[80px] leading-none text-[#D8A936]/15 select-none pointer-events-none -mb-6">"</span>
                    <blockquote className="relative text-[#FAF9F6] text-[15.5px] leading-relaxed flex-1">{currentCarousel.quote}</blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-[#343437]">
                      <p className="font-display text-sm text-[#FAF9F6]">{currentCarousel.name}</p>
                      {currentCarousel.title && <p className="text-[11px] text-[#A9A9A6] mt-1 leading-snug">{currentCarousel.title}</p>}
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[#D8A936] mt-1.5">{currentCarousel.org}</p>
                      {currentCarousel.source && <p className="text-[10px] tracking-[0.18em] uppercase text-[#6E6E6B] mt-1">{currentCarousel.source}</p>}
                      {currentCarousel.theme && (
                        <span className="mt-3 inline-block text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] border border-[#343437] px-2 py-0.5">{currentCarousel.theme}</span>
                      )}
                    </figcaption>
                  </motion.figure>
                </AnimatePresence>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2" role="tablist" aria-label="Testimonial pagination">
                    {carousel.map((item, i) => (
                      <button key={item.id || item.slug || `dot-${i}`} type="button" role="tab" aria-selected={carouselIdx === i} aria-label={`Show testimonial ${i + 1}`} onClick={() => setCarouselIdx(i)} data-testid={`testimonial-dot-${i}`} className={`h-1.5 transition-all ${carouselIdx === i ? "w-8 bg-[#D8A936]" : "w-4 bg-[#343437] hover:bg-[#D8A936]/60"}`} />
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setCarouselIdx((v) => (v - 1 + carousel.length) % carousel.length)} aria-label="Previous testimonial" data-testid="testimonial-prev" className="w-10 h-10 border border-[#343437] flex items-center justify-center text-[#A9A9A6] hover:text-[#D8A936] hover:border-[#D8A936] transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    <button type="button" onClick={() => setCarouselIdx((v) => (v + 1) % carousel.length)} aria-label="Next testimonial" data-testid="testimonial-next" className="w-10 h-10 border border-[#343437] flex items-center justify-center text-[#A9A9A6] hover:text-[#D8A936] hover:border-[#D8A936] transition-colors">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ========= SECTION 8 — Final CTA ========= */}
      <section className="bg-[#0B0B0C] border-t border-[#343437] relative overflow-hidden" aria-labelledby="about-cta-title" data-testid="about-final-cta">
        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ background: "radial-gradient(900px 400px at 88% 20%, rgba(216,169,54,0.10), transparent 60%)" }} aria-hidden />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            <div className="lg:col-span-9">
              <p className="eyebrow">Let's connect</p>
              <h2 id="about-cta-title" className="mt-4 font-display leading-[0.98] tracking-[-0.02em] text-[#FAF9F6]" style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}>
                Different challenges. <br className="hidden md:block" />
                <span className="text-[#D8A936]">Let's build something better.</span>
              </h2>
              <p className="mt-5 text-[#A9A9A6] leading-relaxed max-w-xl text-[15.5px]">Whether it's a leadership opportunity, transformation project, collaboration or simply a conversation about making complex work simpler, I'd love to connect.</p>
            </div>
            <div className="lg:col-span-3 flex flex-col gap-3 lg:items-end">
              <Link to="/contact" data-testid="about-cta-contact" className="btn-primary">
                Let's talk <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
